/**
 * Parametres — réglages utilisateur et de l'église.
 */
import { useState, useEffect } from "react";
import { colors, fonts } from "../styles/tokens";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import { authApi } from "../services/api";
import Card from "../components/Card";
import Input from "../components/Input";
import Button from "../components/Button";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import MesEglisesSection from "../components/MesEglisesSection";
import PagePubliqueSettings from "../components/PagePubliqueSettings";
import SwitchDevise from "../components/SwitchDevise";
import useDevise from "../hooks/useDevise";

export default function Parametres() {
  const { user, isAdmin, updateUser, usingMock } = useAuth();
  const toast = useToast();

  const [profileForm, setProfileForm] = useState({
    full_name: "",
    email: "",
    telephone: "",
    titre: "",
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });

  const sections = [
    { id: "profil", label: "Mon profil" },
    { id: "securite", label: "Sécurité" },
    { id: "notifications", label: "Notifications" },
    ...(isAdmin ? [
      { id: "mes-eglises", label: "Mes Églises" },
      { id: "page-publique", label: "Page publique" },
      { id: "abonnement", label: "Abonnement" },
    ] : []),
  ];
  const [active, setActive] = useState("profil");

  // Synchronise le formulaire dès que l'utilisateur connecté est disponible / change
  useEffect(() => {
    if (user) {
      setProfileForm({
        full_name: user.full_name || "",
        email: user.email || "",
        telephone: user.telephone || "",
        titre: user.titre || "",
      });
    }
  }, [user]);

  /** Validation + appel PUT /auth/me + propagation au contexte. */
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileErrors({});

    // Validation client
    const errs = {};
    if (!profileForm.full_name.trim()) errs.full_name = "Ce champ est obligatoire";
    if (!profileForm.email.trim())     errs.email = "Ce champ est obligatoire";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email.trim())) {
      errs.email = "Format d'email invalide";
    }
    if (Object.keys(errs).length) {
      setProfileErrors(errs);
      return;
    }

    // Mode démo : pas d'API → on met juste à jour le contexte
    if (usingMock) {
      updateUser({
        full_name: profileForm.full_name.trim(),
        email: profileForm.email.trim(),
        telephone: profileForm.telephone.trim() || null,
        titre: profileForm.titre.trim() || null,
      });
      toast.success("✓ Profil mis à jour (mode démo)");
      return;
    }

    setSavingProfile(true);
    try {
      const { data } = await authApi.updateMe({
        full_name: profileForm.full_name.trim(),
        email: profileForm.email.trim(),
        telephone: profileForm.telephone.trim() || null,
        titre: profileForm.titre.trim() || null,
      });
      // Propagation immédiate dans le contexte → Sidebar/header se rafraîchissent
      updateUser(data);
      toast.success("✓ Profil mis à jour");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Erreur lors de l'enregistrement";
      const status = err.response?.status;
      if (status === 409) msg = "Cet email est déjà pris";
      else if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg.replace(/^Value error,\s*/, "");
        // Mappe sur les champs si possible
        const field = detail[0]?.loc?.[detail[0].loc.length - 1];
        if (field === "email") setProfileErrors((p) => ({ ...p, email: msg }));
        if (field === "full_name") setProfileErrors((p) => ({ ...p, full_name: msg }));
      } else if (!err.response) {
        msg = "Erreur de connexion au serveur";
      }
      toast.error(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (passwordForm.next.length < 6) {
      toast.error("Mot de passe trop court (min. 6 caractères)");
      return;
    }
    setPasswordForm({ current: "", next: "", confirm: "" });
    toast.success("Mot de passe mis à jour (mode démo)");
  };

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      <header style={{ marginBottom: "24px" }}>
        <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
          Paramètres
        </h1>
        <p style={{ color: colors.textMuted, fontSize: "14px" }}>
          Gérez votre compte et vos préférences
        </p>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: "20px" }} className="settings-grid">
        {/* Nav latérale */}
        <Card style={{ padding: "8px", height: "fit-content" }}>
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "10px 14px", borderRadius: "8px",
                color: active === s.id ? colors.gold : colors.text,
                background: active === s.id ? colors.goldDim : "transparent",
                fontSize: "14px", fontWeight: active === s.id ? 600 : 500,
                marginBottom: "2px",
              }}
            >
              {s.label}
            </button>
          ))}
        </Card>

        {/* Contenu */}
        <div>
          {active === "profil" && (
            <Card>
              <h2 style={{ fontFamily: fonts.heading, fontSize: "22px", marginBottom: "20px" }}>Profil</h2>
              <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px", padding: "16px", background: colors.surface, borderRadius: "10px" }}>
                <Avatar name={user?.full_name} size="xl" bordered />
                <div>
                  <div style={{ fontWeight: 600, fontSize: "16px" }}>{user?.full_name}</div>
                  {user?.titre && (
                    <div style={{ color: colors.gold, fontSize: "13px", fontWeight: 500, marginTop: "2px" }}>
                      {user.titre}
                    </div>
                  )}
                  <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "8px" }}>{user?.email}</div>
                  <Badge variant={isAdmin ? "gold" : "info"} dot>{user?.role}</Badge>
                </div>
              </div>
              <form onSubmit={handleSaveProfile} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
                  <Input
                    label="Nom complet"
                    value={profileForm.full_name}
                    onChange={(e) => {
                      setProfileForm({ ...profileForm, full_name: e.target.value });
                      if (profileErrors.full_name) setProfileErrors((p) => ({ ...p, full_name: null }));
                    }}
                    required
                    error={profileErrors.full_name}
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => {
                      setProfileForm({ ...profileForm, email: e.target.value });
                      if (profileErrors.email) setProfileErrors((p) => ({ ...p, email: null }));
                    }}
                    required
                    error={profileErrors.email}
                    icon="✉"
                  />
                  <Input
                    label="Téléphone"
                    value={profileForm.telephone}
                    onChange={(e) => setProfileForm({ ...profileForm, telephone: e.target.value })}
                    placeholder="+225 07 11 22 33 44"
                    icon="📞"
                  />
                  <Input
                    label="Titre / Fonction"
                    value={profileForm.titre}
                    onChange={(e) => setProfileForm({ ...profileForm, titre: e.target.value })}
                    placeholder="Pasteur, Ancien, Diacre…"
                  />
                </div>
                <Button
                  type="submit"
                  loading={savingProfile}
                  disabled={savingProfile}
                  style={{ alignSelf: "flex-start", marginTop: "4px" }}
                >
                  Enregistrer les modifications
                </Button>
              </form>
            </Card>
          )}

          {active === "securite" && (
            <Card>
              <h2 style={{ fontFamily: fonts.heading, fontSize: "22px", marginBottom: "20px" }}>Sécurité</h2>
              <form onSubmit={handleChangePassword} style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: 460 }}>
                <Input label="Mot de passe actuel" type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} required />
                <Input label="Nouveau mot de passe" type="password" value={passwordForm.next} onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })} required />
                <Input label="Confirmer le nouveau mot de passe" type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} required />
                <Button type="submit" style={{ alignSelf: "flex-start" }}>Changer le mot de passe</Button>
              </form>
              <hr style={{ border: "none", borderTop: `1px solid ${colors.border}`, margin: "24px 0" }} />
              <h3 style={{ fontFamily: fonts.heading, fontSize: "18px", marginBottom: "8px" }}>Sessions actives</h3>
              <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "12px" }}>
                Cette session : navigateur Chrome — actif maintenant
              </p>
              <Button variant="danger">Déconnecter toutes les autres sessions</Button>
            </Card>
          )}

          {active === "notifications" && (
            <Card>
              <h2 style={{ fontFamily: fonts.heading, fontSize: "22px", marginBottom: "20px" }}>Notifications</h2>
              {[
                { label: "Nouveaux événements", desc: "Soyez alerté lors de la création d'un événement" },
                { label: "Annonces de l'église", desc: "Recevez les annonces officielles" },
                { label: "Rappels de dons", desc: "Confirmation à chaque don enregistré" },
                { label: "Newsletter mensuelle", desc: "Résumé du mois et témoignages" },
              ].map((n, i) => (
                <Toggle key={i} label={n.label} desc={n.desc} defaultChecked={i < 2} />
              ))}
            </Card>
          )}

          {active === "mes-eglises" && isAdmin && <MesEglisesSection />}

          {active === "page-publique" && isAdmin && <PagePubliqueSettings />}

          {active === "abonnement" && isAdmin && <AbonnementSection />}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .settings-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/** Toggle on/off (visuel). */
function Toggle({ label, desc, defaultChecked = false }) {
  const [on, setOn] = useState(defaultChecked);
  return (
    <div
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 0", borderBottom: `1px solid ${colors.borderLight}`,
      }}
    >
      <div>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ color: colors.textMuted, fontSize: "13px" }}>{desc}</div>
      </div>
      <button
        onClick={() => setOn(!on)}
        style={{
          width: 44, height: 24, borderRadius: "999px",
          background: on ? colors.gold : colors.border,
          position: "relative", transition: "all 200ms",
        }}
      >
        <span
          style={{
            position: "absolute", top: 2, left: on ? 22 : 2,
            width: 20, height: 20, borderRadius: "50%",
            background: colors.bg, transition: "left 200ms",
          }}
        />
      </button>
    </div>
  );
}

/** Onglet Abonnement : plan actuel + grille tarifaire dans la devise sélectionnée. */
function AbonnementSection() {
  const { devise, drapeau, nom, prix, afficher } = useDevise();
  const [annuel, setAnnuel] = useState(true);
  const cycle = annuel ? "annuel" : "mensuel";

  const plansAffiches = [
    { key: "communaute", nom: "Communauté", desc: "150 membres · 1 église" },
    { key: "starter",    nom: "Starter",    desc: "500 membres · 3 églises · Bénévoles" },
    { key: "pro",        nom: "Pro",        desc: "Illimité · Tous add-ons inclus" },
    { key: "enterprise", nom: "Enterprise", desc: "Sur mesure · Support dédié" },
  ];

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontFamily: fonts.heading, fontSize: "22px", margin: 0 }}>Abonnement</h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <SwitchDevise />
        </div>
      </div>

      {/* Plan actuel */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.goldDim} 0%, ${colors.card} 100%)`,
        border: `1px solid ${colors.goldBorder}`,
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "20px",
      }}>
        <div style={{ fontFamily: fonts.heading, fontSize: "32px", fontWeight: 700, color: colors.gold, marginBottom: "4px" }}>
          Plan PRO
        </div>
        <div style={{ color: colors.text, fontSize: "14px", marginBottom: "8px" }}>
          Membres illimités · Réseau multi-églises · Tous les add-ons inclus · Support prioritaire
        </div>
        <div style={{ color: colors.gold, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          {afficher(prix("pro", cycle))} {annuel ? "/an" : "/mois"} · {drapeau} {nom}
        </div>
        <Badge variant="success" dot>Actif jusqu'au 31/12/2026</Badge>
      </div>

      {/* Toggle cycle */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <div style={{
          display: "inline-flex",
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: 999,
          padding: 4,
        }}>
          <button
            type="button"
            onClick={() => setAnnuel(false)}
            style={{
              padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 600,
              color: !annuel ? colors.bg : colors.textMuted,
              background: !annuel ? colors.gold : "transparent",
            }}
          >
            Mensuel
          </button>
          <button
            type="button"
            onClick={() => setAnnuel(true)}
            style={{
              padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 600,
              color: annuel ? colors.bg : colors.textMuted,
              background: annuel ? colors.gold : "transparent",
            }}
          >
            Annuel <span style={{ fontSize: 10, opacity: 0.8 }}>-10%</span>
          </button>
        </div>
      </div>

      {/* Grille des plans */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
        {plansAffiches.map((p) => (
          <div key={p.key} style={{
            padding: "16px",
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
          }}>
            <div style={{ color: colors.gold, fontWeight: 700, fontSize: 12, letterSpacing: "0.1em", marginBottom: 6 }}>
              {p.nom.toUpperCase()}
            </div>
            <div style={{ fontFamily: fonts.heading, fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
              {afficher(prix(p.key, cycle))}
            </div>
            <div style={{ color: colors.textMuted, fontSize: 11, marginBottom: 10 }}>
              {annuel ? "/ an" : "/ mois"}
            </div>
            <div style={{ color: colors.textMuted, fontSize: 12, lineHeight: 1.4 }}>
              {p.desc}
            </div>
          </div>
        ))}
      </div>

      <Button variant="ghost">Passer au plan Enterprise</Button>
    </Card>
  );
}
